import { lazy, useEffect, type ComponentType } from "react";
import { useNavigate, useParams, type RouteDef } from "@/router/Router";
import HomePage from "@/pages/HomePage";
import { useAuth } from "@/state/AuthProvider";
const AccountHome = lazy(() => import("@/pages/AccountPage"));
function HomeEntry() {
  const { user } = useAuth();
  return user ? <AccountHome /> : <HomePage />;
}

/** Every page except Home is code-split and loaded on demand. Modules group related pages. */
type Mod = Record<string, ComponentType>;
const from = (load: () => Promise<Mod>, name: string) => lazy(() => load().then((m) => ({ default: m[name] })));

const about = () => import("@/pages/AboutPages") as unknown as Promise<Mod>;
const watch = () => import("@/pages/WatchPages") as unknown as Promise<Mod>;
const devo = () => import("@/pages/DevotionalPages") as unknown as Promise<Mod>;
const bible = () => import("@/pages/BiblePages") as unknown as Promise<Mod>;
const events = () => import("@/pages/EventPages") as unknown as Promise<Mod>;
const places = () => import("@/pages/LocationPages") as unknown as Promise<Mod>;
const connect = () => import("@/pages/ConnectPages") as unknown as Promise<Mod>;
const ministry = () => import("@/pages/MinistryPages") as unknown as Promise<Mod>;
const members = () => import("@/pages/MemberPages") as unknown as Promise<Mod>;

/** Sends old miracleassembly.org (WordPress) URLs to their new homes, keeping links and search results alive. */
function redirect(to: string | ((params: Record<string, string>) => string)): ComponentType {
  return function Redirect() {
    const navigate = useNavigate();
    const params = useParams<Record<string, string>>();
    useEffect(() => {
      navigate(typeof to === "string" ? to : to(params as Record<string, string>), { replace: true });
    }, [navigate, params]);
    return null;
  };
}

export const ROUTES: RouteDef[] = [
  { path: "/", component: HomeEntry },
  // The public landing page remains reachable while a member is signed in.
  { path: "/welcome", component: HomePage },

  { path: "/about", component: from(about, "AboutPage") },
  { path: "/about/pastor", component: from(about, "PastorPage") },
  { path: "/about/pastor-loveth", component: from(about, "PastorLovethPage") },
  { path: "/about/mission", component: from(about, "MissionPage") },
  { path: "/about/vision", component: from(about, "VisionPage") },
  { path: "/about/purpose", component: from(about, "PurposePage") },
  { path: "/about/structure", component: from(about, "StructurePage") },
  { path: "/about/beliefs", component: from(about, "BeliefsPage") },

  { path: "/watch", component: from(watch, "WatchPage") },
  { path: "/watch/live", component: from(watch, "LivePage") },
  { path: "/watch/sermons", component: from(watch, "SermonsPage") },
  { path: "/watch/sermons/:id", component: from(watch, "SermonPage") },

  { path: "/devotionals", component: from(devo, "DevotionalsPage") },
  { path: "/devotionals/:id", component: from(devo, "DevotionalPage") },

  { path: "/bible", component: from(bible, "BiblePage") },
  { path: "/bible/:book", component: from(bible, "BibleBookPage") },
  { path: "/bible/:book/:chapter", component: from(bible, "BibleChapterPage") },

  { path: "/events", component: from(events, "EventsPage") },
  { path: "/events/:id", component: from(events, "EventPage") },
  { path: "/calendar", component: from(events, "CalendarPage") },

  { path: "/locations", component: from(places, "LocationsPage") },
  { path: "/care-groups", component: from(places, "CareGroupsPage") },
  { path: "/care-groups/posts/:id", component: from(places, "CareGroupPostPage") },

  { path: "/new", component: from(connect, "NewHerePage") },
  { path: "/connect", component: from(connect, "ConnectPage") },
  { path: "/connect/believers-academy", component: from(connect, "AcademyPage") },
  { path: "/connect/join-a-team", component: from(connect, "JoinTeamPage") },
  { path: "/testimonies", component: from(connect, "TestimoniesPage") },
  { path: "/contact", component: from(connect, "ContactPage") },

  { path: "/expressions", component: from(ministry, "ExpressionsPage") },
  { path: "/expressions/:slug", component: from(ministry, "ExpressionPage") },
  { path: "/pastors-circle", component: from(ministry, "PastorsCirclePage") },
  { path: "/give", component: from(ministry, "GivePage") },
  { path: "/care", component: from(ministry, "CarePage") },
  { path: "/shop", component: from(ministry, "ShopPage") },

  { path: "/posts", component: lazy(() => import("@/pages/PostsPage")) },
  { path: "/posts/:id", component: lazy(() => import("@/pages/PostsPage")) },
  { path: "/account", component: lazy(() => import("@/pages/AccountPage")) },
  { path: "/messages", component: from(members, "MessagesPage") },
  { path: "/messages/:id", component: from(members, "MessagesPage") },
  { path: "/members/:id", component: from(members, "MemberProfilePage") },
  { path: "/admin/birthdays", component: from(members, "BirthdaysPage") },

  // Legacy WordPress URLs
  { path: "/our-mission", component: redirect("/about/mission") },
  { path: "/our-vision", component: redirect("/about/vision") },
  { path: "/our-purpose", component: redirect("/about/purpose") },
  { path: "/our-team-structure", component: redirect("/about/structure") },
  { path: "/believers-academy", component: redirect("/connect/believers-academy") },
  { path: "/devotional", component: redirect("/devotionals") },
  { path: "/join-a-team", component: redirect("/connect/join-a-team") },
  { path: "/mili", component: redirect("/expressions/mili") },
  { path: "/pams", component: redirect("/expressions/pams") },
  { path: "/vwfi", component: redirect("/expressions/vwfi") },
  { path: "/singles-fellowship", component: redirect("/expressions/singles-cohort") },
  { path: "/gods-leading-ladies", component: redirect("/expressions/gl2") },
  { path: "/our-events", component: redirect("/events") },
  { path: "/event/:slug", component: redirect((p) => `/events/${p.slug}`) },
  { path: "/contacts", component: redirect("/locations") },
  { path: "/timetable", component: redirect("/calendar") },

  { path: "*", component: lazy(() => import("@/pages/NotFoundPage")) },
];
